import { memo, useCallback, useEffect, useState } from 'react'
import { GoogleMap, Marker, useJsApiLoader, InfoWindow } from '@react-google-maps/api'
import { useTranslation } from 'react-i18next'

// local imports
import { IAllLocationList } from 'types/LocationType'
import Loader from 'components/ui/Loader'
import NoContent from 'components/ui/NoContent'
import MapSearchBox from './search-box/SearchBox'
import { InfoWindowContent, MapViewContainer, SearchBoxWrapper, StyledLink } from './styled'

/* assets */
import commonOffice from 'assets/icons/common-office-marker.svg'
import firmOffice from 'assets/icons/firm-office-marker.svg'
import loaderLogo from 'assets/icons/location-gray.svg'

// variables -
const containerStyle = { width: '100%', height: 'calc(100vh - 145px)', borderRadius: '8px 8px 0 0' }
const center = { lat: 38.963748, lng: 35.243359 } // center of Türkiye
const options: google.maps.MapOptions = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  controlSize: 35,
  minZoom: 2,
  maxZoom: 18,
  clickableIcons: false,
}

interface MapProps {
  country: google.maps.LatLngLiteral
  city?: google.maps.LatLngLiteral
  district?: google.maps.LatLngLiteral
  locationList: IAllLocationList[] | undefined
  openSidebar: (e: any, location: IAllLocationList) => void
}

function MapContainer({ country, city, district, locationList, openSidebar }: MapProps) {
  const { t } = useTranslation()
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [activeMarker, setActiveMarker] = useState<number | null>(null)

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'office-free-map',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
  })

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    // map?.moveCamera({ center: center, zoom: 8, heading: 10, tilt: 10 })
    map.setCenter(center)
    map.setZoom(6)
    setMap(map)
  }, [])

  const onUnmount = useCallback(function callback(map) {
    setMap(null)
  }, [])

  /* ########### Marker related functions ###########  */
  const handleActiveMarker = (e: google.maps.MapMouseEvent, marker: number) => {
    if (marker === activeMarker) return
    if (e.latLng) map?.panTo(e.latLng)
    setActiveMarker(marker)
  }
  const resetActiveMarker = () => setActiveMarker(null)

  const handleMapClick = (e: google.maps.MapMouseEvent) => resetActiveMarker

  const setMarkerType = (isFirmOffice: boolean) => {
    if (isFirmOffice)
      return { url: firmOffice, anchor: new google.maps.Point(25, 50), scaledSize: new google.maps.Size(50, 50) }
    else return { url: commonOffice, anchor: new google.maps.Point(25, 50), scaledSize: new google.maps.Size(50, 50) }
  }

  /* Move Camera to bounds (Country - City - District) */
  useEffect(() => {
    if (!!map) {
      if (country.lat !== 0 && country.lng !== 0 && city?.lat === 0 && district?.lat === 0) {
        map?.setCenter(country)
        map?.setZoom(6)
      }
      if (city && city?.lat !== 0 && city?.lng !== 0) {
        map?.setCenter(city)
        map?.setZoom(10)
      }
      if (district && district?.lat !== 0 && district?.lng !== 0) {
        map?.setCenter(district)
        map?.setZoom(14)
      }
    }
  }, [country, map, city, district])

  /* Map Search Box (address) */
  const [selectedLocation, setSelectedLocation] = useState<IAllLocationList>()
  useEffect(() => {
    resetActiveMarker()
    if (selectedLocation) {
      const coordinates = { lat: selectedLocation?.latitude || 0, lng: selectedLocation?.longitude || 0 }
      const bounds = new window.google.maps.LatLngBounds(coordinates)
      map?.fitBounds(bounds)
      setActiveMarker(selectedLocation.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation])

  /* Contants - Variables */
  const locationIds = locationList?.map((loc) => loc.id)
  const selectedHasInView = locationIds?.includes(selectedLocation?.id || 0)
  // const markerHasAnimation = (markerId: number) =>
  //   activeMarker === markerId ? google.maps.Animation.BOUNCE : undefined

  /* Render Functions =>  Show Locations - Marker */
  function renderMarkers(dt: IAllLocationList): JSX.Element {
    return (
      <Marker
        onUnmount={resetActiveMarker}
        // animation={markerHasAnimation(dt.id)}
        icon={setMarkerType(dt.isMyFirmLocation)}
        key={dt.id}
        position={{ lat: dt.latitude, lng: dt.longitude }}
        onClick={(e) => handleActiveMarker(e, dt.id)}>
        {activeMarker === dt.id ? (
          <InfoWindow onCloseClick={resetActiveMarker}>
            <InfoWindowContent>
              <div className="title">{dt.name}</div>
              <div className="subtitle">{dt.firmName}</div>
              <div className="desc">{dt.address}</div>
              <StyledLink to="/" onClick={(e) => openSidebar(e, dt)}>
                {t('buttons.go-to-floor')}
              </StyledLink>
            </InfoWindowContent>
          </InfoWindow>
        ) : null}
      </Marker>
    )
  }

  /* Render => Selected Location Marker */
  function renderSelectedLocationMarker(location: IAllLocationList) {
    return (
      <Marker
        onUnmount={resetActiveMarker}
        // animation={markerHasAnimation(location.id)}
        icon={setMarkerType(location.isMyFirmLocation)}
        key={`${location.id}_selected`}
        position={{ lat: location.latitude, lng: location.longitude }}
        onClick={(e) => handleActiveMarker(e, location.id)}>
        {activeMarker === location.id ? (
          <InfoWindow onCloseClick={resetActiveMarker}>
            <InfoWindowContent>
              <div className="title">{location.name}</div>
              <div className="subtitle">{location.firmName}</div>
              <div className="desc">{location.address}</div>
              <StyledLink to="/" onClick={(e) => openSidebar(e, location)}>
                {t('buttons.go-to-floor')}
              </StyledLink>
            </InfoWindowContent>
          </InfoWindow>
        ) : null}
      </Marker>
    )
  }

  /* Handle Error Handling */
  if (loadError) return <NoContent message={t('failed.map-error-2')} />

  return (
    <MapViewContainer>
      {isLoaded ? (
        <GoogleMap
          onClick={handleMapClick}
          options={options}
          mapContainerStyle={containerStyle}
          onLoad={onLoad}
          onUnmount={onUnmount}>
          <SearchBoxWrapper>
            <MapSearchBox selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} />
          </SearchBoxWrapper>

          {/* Marker - İnfoWindow */}
          {!!locationList?.length &&
            locationList?.map((dt) => {
              if (!dt.latitude || !dt.longitude) return null
              return renderMarkers(dt)
            })}

          {!selectedHasInView && !!selectedLocation && !!selectedLocation.latitude && !!selectedLocation.longitude
            ? renderSelectedLocationMarker(selectedLocation)
            : null}
        </GoogleMap>
      ) : (
        <Loader src={loaderLogo} />
      )}
    </MapViewContainer>
  )
}

export default memo(MapContainer)
